import { useState, useEffect, useRef } from "react";
import { TokenPurchaseModal } from "../billing/TokenPurchaseModal";
import { WorkspaceMembersModal } from "../workspace/WorkspaceMembersModal";
import { LangDropdown } from "../../shared/ui/LangDropdown";

function SignOutSVG() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 2H2a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h3" />
      <polyline points="9 9 12 6.5 9 4" />
      <line x1="12" y1="6.5" x2="5" y2="6.5" />
    </svg>
  );
}
function GearSVG() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6.5" cy="6.5" r="1.75" />
      <path d="M6.5 1.2v1.1M6.5 10.7v1.1M1.2 6.5h1.1M10.7 6.5h1.1M2.9 2.9l.78.78M9.32 9.32l.78.78M2.9 10.1l.78-.78M9.32 3.68l.78-.78" />
    </svg>
  );
}
function SupportSVG() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 1.5H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h3l1.5 2 1.5-2h3a1 1 0 0 0 1-1v-6a1 1 0 0 0-1-1z" />
      <line x1="6.5" y1="4" x2="6.5" y2="6" />
      <circle cx="6.5" cy="7.5" r="0.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

function AccountMenu({ user, profile, onSignOut, onClose, onRefreshProfile, lang, setLang,
  workspaces, currentWorkspace, onSwitchWorkspace, onCreateWorkspace, onWorkspaceDeleted, onOpenHistory,
  onOpenSettings, onOpenSupport, t, chassisCount = 0, recentChassisNames = [] }: any) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [workspaceExpanded, setWorkspaceExpanded] = useState(false);
  const [managingWorkspace, setManagingWorkspace] = useState(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  const tokens = typeof profile?.token_balance === "number"
    ? profile.token_balance % 1 === 0
      ? profile.token_balance.toString()
      : profile.token_balance.toFixed(2)
    : "—";

  const wsTokens = currentWorkspace?.token_balance;
  const displayTokens = currentWorkspace
    ? (typeof wsTokens === "number" ? (wsTokens % 1 === 0 ? wsTokens : wsTokens.toFixed(2)) : "—")
    : tokens;
  const contextLabel = currentWorkspace ? currentWorkspace.name : (t?.menuPersonal || 'Personal');
  const roleLabel = currentWorkspace?.role?.toUpperCase() || "";

  // Avatar initials — prefer display_name, fall back to email
  const rawSrc = profile?.display_name?.trim() || user?.email || '?';
  const words = rawSrc.split(/\s+/).filter(Boolean);
  const initials = words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : rawSrc.slice(0, 2).toUpperCase();

  if (buyOpen) return (
    <TokenPurchaseModal
      user={user} profile={profile}
      currentWorkspace={currentWorkspace}
      onClose={() => setBuyOpen(false)}
      onTokensAdded={() => { onRefreshProfile(); setBuyOpen(false); onClose(); }}
    />
  );

  if (managingWorkspace) return (
    <WorkspaceMembersModal
      workspace={managingWorkspace} user={user} t={t} lang={lang}
      userRole={workspaces.find((w: any) => w.id === (managingWorkspace as any).id)?.role}
      onClose={() => setManagingWorkspace(null)}
      onDeleted={(wsId: string) => {
        // Switch to Personal first so currentWorkspace never points at a deleted
        // row, then drop it from the list, then close both modals.
        onSwitchWorkspace(null);
        onWorkspaceDeleted?.(wsId);
        setManagingWorkspace(null);
        onClose();
      }}
    />
  );

  return (
    <div ref={ref} style={{ position: "absolute", right: 0, top: "calc(100% + 8px)",
      background: "#fff", border: "1px solid #000", minWidth: 220, maxWidth: 280, width: 280, zIndex: 300,
      boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>

      {/* User info — avatar + name + lang dropdown */}
      <div style={{ padding: "12px 14px", borderBottom: "1px solid #e8e8e8" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#000",
              fontFamily: "'Georgia', serif", overflow: "hidden",
              textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {profile?.display_name || user.email}
            </div>
          </div>
        </div>
      </div>

      {/* Workspace switcher */}
      <div style={{ borderBottom: "1px solid #e8e8e8" }}>
        <div role="button" tabIndex={0}
          onClick={() => setWorkspaceExpanded(o => !o)}
          onKeyDown={e => { if (e.key === "Enter" || e.key === " ") {
            e.preventDefault(); setWorkspaceExpanded(o => !o); } }}
          style={{ width: "100%", padding: "10px 14px", background: "none",
            cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: 8, boxSizing: "border-box" }}>
          <div>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#aaa",
              letterSpacing: "0.12em", marginBottom: 2 }}>
              {t?.menuWorkspace || 'WORKSPACE'}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontFamily: "'Courier New', monospace", fontSize: 11, fontWeight: 900,
                color: "#000" }}>{contextLabel}</span>
              {roleLabel && <span style={{ fontFamily: "'Courier New', monospace", fontSize: 8,
                color: "#888", border: "1px solid #ddd", padding: "1px 4px",
                whiteSpace: "nowrap" }}>{roleLabel}</span>}
            </div>
          </div>
          <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {currentWorkspace &&
             (currentWorkspace.role === "owner" || currentWorkspace.role === "co-owner") && (
              <button
                onClick={e => { e.stopPropagation(); setManagingWorkspace(currentWorkspace); }}
                title={t?.workspaceConfigure || 'Configure workspace'}
                style={{ background: "none", border: "none", padding: 2, cursor: "pointer",
                  fontFamily: "monospace", fontSize: 13, color: "#aaa",
                  display: "flex", alignItems: "center" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#000"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "#aaa"; }}>
                ⚙
              </button>
            )}
            <span style={{ fontFamily: "monospace", fontSize: 10, color: "#888" }}>
              {workspaceExpanded ? "▲" : "▼"}
            </span>
          </span>
        </div>
        {workspaceExpanded && (
          <div style={{ borderTop: "1px solid #f0f0f0" }}>
            <button onClick={() => { onSwitchWorkspace(null); setWorkspaceExpanded(false); }}
              style={{ width: "100%", padding: "8px 14px 8px 22px", background: !currentWorkspace ? "#f8f8f8" : "none",
                border: "none", cursor: "pointer", textAlign: "left", fontFamily: "'Courier New', monospace",
                fontSize: 11, color: "#000", fontWeight: !currentWorkspace ? 900 : 400,
                borderLeft: !currentWorkspace ? "3px solid #000" : "3px solid transparent" }}
              onMouseEnter={e => { if (currentWorkspace) e.currentTarget.style.background = "#f5f5f5"; }}
              onMouseLeave={e => { if (currentWorkspace) e.currentTarget.style.background = "none"; }}>
              {t?.menuPersonal || 'Personal'}
            </button>
            {workspaces.map((ws: any) => (
              <button key={ws.id}
                onClick={() => { onSwitchWorkspace((ws as any)); setWorkspaceExpanded(false); }}
                style={{ width: "100%", padding: "8px 10px 8px 22px",
                  background: currentWorkspace?.id === ws.id ? "#f8f8f8" : "none",
                  border: "none", cursor: "pointer", textAlign: "left", fontFamily: "'Courier New', monospace",
                  fontSize: 11, color: "#000", fontWeight: currentWorkspace?.id === ws.id ? 900 : 400,
                  borderLeft: currentWorkspace?.id === ws.id ? "3px solid #000" : "3px solid transparent",
                  display: "flex", justifyContent: "space-between", alignItems: "center" }}
                onMouseEnter={e => { if (currentWorkspace?.id !== ws.id) e.currentTarget.style.background = "#f5f5f5"; }}
                onMouseLeave={e => { if (currentWorkspace?.id !== ws.id) e.currentTarget.style.background = "none"; }}>
                <span>{ws.name}</span>
                <span style={{ fontSize: 8, color: "#888", border: "1px solid #eee",
                  padding: "1px 4px", marginLeft: 6, whiteSpace: "nowrap" }}>
                  {ws.role?.toUpperCase()}
                </span>
              </button>
            ))}
            <button onClick={() => { onCreateWorkspace(); setWorkspaceExpanded(false); onClose(); }}
              style={{ width: "100%", padding: "8px 14px 8px 22px", background: "none", border: "none",
                borderTop: "1px solid #f0f0f0", cursor: "pointer", textAlign: "left",
                fontFamily: "'Courier New', monospace", fontSize: 11, color: "#888" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#f5f5f5"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
              {t?.menuAddWorkspace || '+ Workspace'}
            </button>
          </div>
        )}
      </div>

      {/* Chassis count + last name */}
      <button onClick={() => { onOpenHistory(); onClose(); }}
        style={{ width: "100%", padding: "9px 14px", background: "none", border: "none",
          borderBottom: "1px solid #e8e8e8", cursor: "pointer", textAlign: "left" }}
        onMouseEnter={e => e.currentTarget.style.background = "#f0f0f0"}
        onMouseLeave={e => e.currentTarget.style.background = "none"}>
        <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#aaa",
            letterSpacing: "0.12em", marginBottom: 4 }}>
          {t?.menuChassis || 'CHASSIS'} ({chassisCount})
        </div>
        <div style={{ fontFamily: "'Courier New', monospace", fontSize: 11,
            fontWeight: 900, color: recentChassisNames.length > 0 ? "#000" : "#aaa",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {recentChassisNames.length > 0 ? recentChassisNames.join(", ") : "—"}
        </div>
      </button>

      {/* Token balance */}
      <div style={{ padding: "12px 14px", borderBottom: "1px solid #e8e8e8", background: "#f8f8f8" }}>
        <div style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: "#aaa",
          letterSpacing: "0.12em", marginBottom: 4 }}>
          {t?.menuTokenBalance || 'TOKEN BALANCE'}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: "#000", lineHeight: 1,
            fontFamily: "'Courier New', monospace" }}>{displayTokens}</span>
          <button onClick={() => setBuyOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer",
              fontFamily: "'Courier New', monospace", fontSize: 10, fontWeight: 900,
              color: "#000" }}>
            {t?.menuBuyMore || '+ Buy More'}
          </button>
        </div>
      </div>

      {/* Actions — 4-button row: Settings | Support | EN | Sign Out */}
      <div style={{ display: "flex", borderTop: "1px solid #e8e8e8" }}>
        {[
          { key: "settings", icon: <GearSVG />,    action: () => { onOpenSettings?.(); onClose(); } },
          { key: "support",  icon: <SupportSVG />, action: () => { onOpenSupport?.(); onClose(); } },
        ].map((item, idx) => (
          <div key={item.key} style={{ display: "contents" }}>
            {idx > 0 && <div style={{ width: 1, background: "#e8e8e8", alignSelf: "stretch" }} />}
            <button onClick={item.action}
              style={{ flex: 1, padding: "10px 6px", background: "none", border: "none",
                cursor: "pointer", display: "flex", flexDirection: "column",
                alignItems: "center", gap: 4, color: "#000" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}>
              {item.icon}
            </button>
          </div>
        ))}
        <div style={{ width: 1, background: "#e8e8e8", alignSelf: "stretch" }} />
        <div style={{ flex: 1, display: "flex", alignItems: "center",
            justifyContent: "center", padding: "10px 6px" }}>
          <LangDropdown lang={lang} setLang={setLang} bare={true} />
        </div>
        <div style={{ width: 1, background: "#e8e8e8", alignSelf: "stretch" }} />
        <button onClick={() => { onSignOut(); onClose(); }}
          style={{ flex: 1, padding: "10px 6px", background: "none", border: "none",
            cursor: "pointer", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 4, color: "#000" }}
          onMouseEnter={e => e.currentTarget.style.background = "#f5f5f5"}
          onMouseLeave={e => e.currentTarget.style.background = "none"}>
          <SignOutSVG />
        </button>
      </div>
    </div>
  );
}

export { AccountMenu };
